import { type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types/customer";
import { InstallChainCard } from "@/components/properties/install-chain-card";
import { LettersAboutCard } from "@/components/properties/letters-about-card";
import { NameplateCard } from "@/components/properties/nameplate-card";
import { StatusPanel } from "@/components/properties/status-panel";
import { TestHistoryList } from "@/components/properties/test-history-list";
import { buttonClasses, ContactBlock, LoadError, PageTitle, Skeleton } from "@/components/ui";
import { useActiveMembership, useMyCustomerContext } from "@/hooks/use-customer-context";
import {
  useCustomerInstallation,
  useCustomerInventoryInstallations,
} from "@/hooks/use-customer-devices";
import { useCustomerFacility } from "@/hooks/use-customer-premises";
import { REQUEST_PAGE_LIMIT, useCustomerTestRequests } from "@/hooks/use-customer-test-requests";
import { useCustomerSubmissions } from "@/hooks/use-customer-tests";
import { useDeviceStatusText } from "@/hooks/use-device-status-text";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import {
  propertiesLink,
  propertyLink,
  requestLink,
  requestsLink,
  requestTestLink,
} from "@/lib/destinations";
import { deviceLabel, deviceStatus, type StatusKind } from "@/lib/device-label";
import { openRequestIndex, requestBlockReason } from "@/lib/home-standing";
import { brandingFromMembership } from "@/lib/portal-memory";
import { present } from "@/lib/present";

type CustomerInstallation = components["schemas"]["CustomerInstallation"];
type CustomerFacility = components["schemas"]["CustomerFacility"];
type CustomerMembership = components["schemas"]["CustomerMembership"];
type CustomerSubmissionListItem = components["schemas"]["CustomerSubmissionListItem"];
type CustomerTestRequest = components["schemas"]["CustomerTestRequest"];

export const Route = createFileRoute("/_customer/properties/devices/$installationId")({
  component: DeviceRecordPage,
});

const PAGE = "flex flex-col gap-5 lg:gap-6";
const GRID = "grid items-start gap-6 lg:grid-cols-[1fr_340px]";
const COLUMN = "flex min-w-0 flex-col gap-4.5 lg:gap-5.5";
const SECTION_TITLE = "mb-2.5 text-lg font-semibold tracking-[-0.01em] text-ink";
const REQUEST_LINK =
  "inline-flex min-h-11 items-center self-start text-sm font-medium text-brand-fg underline-offset-2 hover:underline lg:min-h-9";

// The request feed Home reads, so the record shares its cache entry. It takes
// no account: the device's own facility narrows it.
const REQUEST_PARAMS = { limit: REQUEST_PAGE_LIMIT };

// A failed, past-due or never-tested device needs a test: its request is the
// page's primary action.
const NEEDS_TEST: ReadonlySet<StatusKind> = new Set<StatusKind>([
  "failed",
  "pastDue",
  "neverTested",
]);

// The loaded page's shape in static blocks: the title, the status and the
// nameplate, then the history.
const LOADING = (
  <div className={PAGE}>
    <div>
      <Skeleton height={34} width="55%" />
      <Skeleton height={18} width="30%" className="mt-2" />
    </div>
    <div className={GRID}>
      <div className={COLUMN}>
        <Skeleton variant="card" height={190} />
        <section>
          <Skeleton width={120} height={20} className="mb-2.5" />
          <div className="divide-y divide-line rounded-card border border-line bg-surface">
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex gap-3 px-3.5 py-3 lg:px-5.5">
                <Skeleton width={92} className="shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton width="30%" />
                  <Skeleton width="50%" height={12} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
      <div className={COLUMN}>
        <Skeleton variant="card" height={160} />
        <Skeleton variant="card" height={140} />
      </div>
    </div>
  </div>
);

function DeviceRecordPage() {
  const { t } = useTranslation("customer");
  const { installationId } = Route.useParams();
  const active = useActiveMembership();
  // Every read keys on the device itself, never on the active account: a record
  // reached from another of the person's accounts (after a switch, or through a
  // link) reads whole, and RLS fences each read to what the person can see.
  const installationRead = useCustomerInstallation(installationId);
  const installation = installationRead.data;
  const facilityId = installation?.facility_id ?? "";
  const inventoryId = installation?.device_inventory_id ?? "";
  const facilityRead = useCustomerFacility(facilityId);
  const requestsRead = useCustomerTestRequests(REQUEST_PARAMS);
  const submissionsRead = useCustomerSubmissions({ device_installation_id: installationId });
  const chainRead = useCustomerInventoryInstallations(inventoryId);

  // A 400 is a malformed id (a truncated link): no read of it can succeed, so
  // it reads as not found, as a 404 does.
  const missing = installationRead.error?.status === 404 || installationRead.error?.status === 400;
  if (missing) {
    return (
      <div
        role="alert"
        className="rounded-card border border-line bg-surface p-4 shadow-card lg:p-5"
      >
        <h1 className="text-lg font-semibold tracking-[-0.01em] text-ink">
          {t("deviceDetail.notFound")}
        </h1>
        <Link {...propertiesLink()} className={`mt-3 ${buttonClasses("secondary")}`}>
          {t("properties.title")}
        </Link>
      </div>
    );
  }

  // The device's own read settles first: the history read keyed by a malformed
  // id fails too, and must not report a retryable failure before the not-found.
  if (installationRead.isPending) return LOADING;

  if (
    installationRead.isError ||
    facilityRead.isError ||
    requestsRead.isError ||
    submissionsRead.isError ||
    chainRead.isError
  ) {
    return (
      <LoadError
        asPage
        title={t("deviceDetail.error")}
        body={t("loadError.body")}
        onRetry={() => {
          // A read waiting on the device's own ids has nothing to refetch
          // until the device is loaded.
          void installationRead.refetch();
          if (facilityId) void facilityRead.refetch();
          void requestsRead.refetch();
          void submissionsRead.refetch();
          if (inventoryId) void chainRead.refetch();
        }}
      />
    );
  }

  if (
    !installation ||
    facilityRead.isLoading ||
    requestsRead.isPending ||
    submissionsRead.isPending ||
    chainRead.isLoading
  ) {
    return LOADING;
  }

  return (
    <DeviceRecord
      installation={installation}
      facility={facilityRead.data}
      requests={requestsRead.data?.items ?? []}
      submissions={submissionsRead.data?.items ?? []}
      chain={chainRead.data?.items ?? []}
      active={active}
    />
  );
}

interface DeviceRecordProps {
  installation: CustomerInstallation;
  facility: CustomerFacility | undefined;
  /** The person's request feed, across their accounts. */
  requests: readonly CustomerTestRequest[];
  submissions: readonly CustomerSubmissionListItem[];
  chain: readonly CustomerInstallation[];
  active: CustomerMembership | null;
}

function DeviceRecord({
  installation,
  facility,
  requests,
  submissions,
  chain,
  active,
}: DeviceRecordProps) {
  const { t } = useTranslation("customer");
  const isDesktop = useIsDesktop();
  const { data: context } = useMyCustomerContext();
  // The device belongs to its OWN utility, whichever account is active: the
  // membership naming the org that owns it names the utility, dates the page
  // and gives the program contact. The active membership stands in only when
  // no membership names that org.
  const owning = context?.memberships.find((m) => m.org_id === installation.org_id) ?? active;
  const org = owning?.org_name ?? "";
  const timezone = owning?.org_timezone;

  const title =
    deviceLabel({
      sizeLabel: installation.size_label,
      assemblyType: installation.assembly_type,
      manufacturerLabel: installation.manufacturer_label,
      modelLabel: installation.model_label,
    }) || t("deviceRow.unnamed");
  const location = present(installation.installation_location_description);
  const serial = present(installation.serial_number);
  const serialText = serial ? t("deviceRow.serial", { serial }) : null;
  const where =
    location || serialText ? (
      <>
        {location}
        {serialText && (
          <span className="hidden lg:inline">{location ? `, ${serialText}` : serialText}</span>
        )}
      </>
    ) : undefined;
  const parent = facility
    ? { label: facility.name, link: propertyLink(installation.facility_id) }
    : { label: t("properties.title"), link: propertiesLink() };

  const sections: Record<string, ReactNode> = {
    status: (
      <DeviceStatusPanel
        installation={installation}
        facility={facility}
        requests={requests}
        org={org}
        timezone={timezone}
      />
    ),
    nameplate: <NameplateCard installation={installation} />,
    history: (
      <section>
        <h2 className={SECTION_TITLE}>{t("deviceDetail.history")}</h2>
        {submissions.length > 0 ? (
          <TestHistoryList submissions={submissions} timezone={timezone} />
        ) : (
          <p className="text-sm text-muted">{t("deviceDetail.historyEmpty", { org })}</p>
        )}
      </section>
    ),
    letters: (
      <LettersAboutCard
        title={t("deviceDetail.letters")}
        emptyText={t("deviceDetail.lettersEmpty", { org })}
        orgName={org}
        timezone={timezone}
        facilityId={installation.facility_id}
        filter={{ device_installation_id: installation.id }}
      />
    ),
    chain: chain.some((row) => row.id !== installation.id) ? (
      <InstallChainCard installations={chain} orgTimezone={timezone} />
    ) : null,
    contact: owning ? <ContactBlock branding={brandingFromMembership(owning)} /> : null,
  };
  const column = (names: readonly string[]) =>
    names
      .filter((name) => sections[name] !== null)
      .map((name) => (
        <div key={name} data-record-section={name}>
          {sections[name]}
        </div>
      ));

  return (
    <div className={PAGE}>
      <PageTitle
        title={title}
        sub={where}
        back={parent}
        trail={[
          { label: t("properties.title"), link: propertiesLink() },
          parent,
          { label: location ?? title },
        ]}
      />
      {isDesktop ? (
        <div className={GRID}>
          <div className={COLUMN}>{column(["nameplate", "history", "chain"])}</div>
          <div className={COLUMN}>{column(["status", "letters", "contact"])}</div>
        </div>
      ) : (
        <div className={COLUMN}>
          {column(["status", "nameplate", "history", "letters", "chain", "contact"])}
        </div>
      )}
    </div>
  );
}

function DeviceStatusPanel({
  installation,
  facility,
  requests,
  org,
  timezone,
}: {
  installation: CustomerInstallation;
  facility: CustomerFacility | undefined;
  requests: readonly CustomerTestRequest[];
  org: string;
  timezone: string | undefined;
}) {
  const { t } = useTranslation("customer");
  // The device's earliest open request at its own facility, whichever of the
  // person's accounts made it.
  const openRequest =
    openRequestIndex(requests, facility ? [facility] : []).get(installation.id) ?? null;
  const status = deviceStatus(installation, openRequest);
  const text = useDeviceStatusText(status, timezone);
  const reason = requestBlockReason(installation, openRequest);
  const counted = status.kind !== "outOfService" && status.kind !== "removed";
  const request = openRequest
    ? requests.find((candidate) => candidate.id === openRequest.requestId)
    : undefined;

  return (
    <StatusPanel
      status={status}
      timezone={timezone}
      explanation={t(`deviceDetail.explain.${status.kind}`, { org })}
      action={
        counted && reason === null ? (
          <Link
            {...requestTestLink({
              facilityId: installation.facility_id,
              installationIds: [installation.id],
            })}
            className={buttonClasses(NEEDS_TEST.has(status.kind) ? "primary" : "secondary")}
          >
            {t("deviceDetail.requestTest")}
          </Link>
        ) : undefined
      }
    >
      {text.requestedLine ? (
        <>
          <p className="text-sm font-medium text-tone-blue">{text.requestedLine}</p>
          <Link {...(request ? requestLink(request) : requestsLink())} className={REQUEST_LINK}>
            {t("deviceDetail.seeRequest")}
          </Link>
        </>
      ) : counted && reason === "no_due_date" ? (
        <p className="text-sm text-body">{t("deviceDetail.noDueDate", { org })}</p>
      ) : undefined}
    </StatusPanel>
  );
}
